import ActiveOverlay from 'app/components/partials/active-overlay';
import Button from './button';
import CallToAction from './call-to-action';
import classnames from 'classnames';
import ContentSet from './content-set';
import Data from 'app/components/partials/data';
import env from 'config/env';
import Faq from './faq';
import FileDownload from './file-download';
import Flexi from './flexi';
import ImageCarousel from './image-carousel';
import ImageGallery from './image-gallery';
import Jotform from './jotform';
import LatestNews from './latest-news';
import PagebreakQuote from './pagebreak-quote';
import { POST_MESSAGE_TYPE } from 'app/utilities/post-message-type';
import Pricing from './pricing';
import PropTypes from 'prop-types';
import Table from './table';
import TextContent from './text-content';
import { useLocation } from 'react-router-dom';
import Video from './video';
import React, { useEffect, useRef, useState } from 'react';

const imageCarouselConfig = {
    slideAnimationDuration: 600,
    slideRotationAnimation: 9000
};

const renderBlock = (block, blockProps) => {
    const { blockIndex } = blockProps;

    switch (block.type) {
        case 'button':
            return <Button key={blockIndex} {...blockProps} {...block.data} />;
        case 'calltoaction':
            return <CallToAction key={blockIndex} {...blockProps} {...block} pageTitle={blockProps.pageTitle} />;
        case 'carousel':
            return <ImageCarousel key={blockIndex} {...blockProps} items={block.data} {...imageCarouselConfig} pageTitle={blockProps.pageTitle} />;
        case 'gallery':
            return <ImageGallery key={blockIndex} {...blockProps} items={block.data} pageTitle={blockProps.pageTitle} />;
        case 'content':
        case 'contentaside':
        case 'contentquote':
            return <TextContent key={blockIndex} {...blockProps} {...block} />;
        case 'contentset':
        case 'aboutus':
            return <ContentSet key={blockIndex} {...blockProps} {...block.data} type={block.type} />;
        case 'faq':
            return <Faq key={blockIndex} {...blockProps} items={block.data} />;
        case 'flexi':
            return <Flexi key={blockIndex} {...blockProps} items={block.data} />;
        case 'file':
            return <FileDownload key={blockIndex} {...blockProps} {...block.data} />;
        case 'jotform':
            return <Jotform key={blockIndex} {...blockProps} {...block.data} />;
        case 'ticketpricing':
            return <Pricing key={blockIndex} {...blockProps} data={block.data} />;
        case 'video':
            return <Video key={blockIndex} {...blockProps} {...block} />;
        case 'pagebreakquote':
            return <PagebreakQuote key={blockIndex} {...blockProps} {...block.data} />;
        case 'table':
            return <Table key={blockIndex} {...blockProps} {...block.data} />;
        case 'latestnews':
            return <LatestNews key={blockIndex} {...blockProps} {...block.data} />;
        default:
            return (
                <Data key={blockIndex} {...blockProps} title={`Missing content block type: ${block.type}`} />
            );
    }
};

const ContentBlocks = ({ data, pageTitle, hasHeroCarousel }) => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const isLivePreview = location.pathname.startsWith('/livepreview/') && searchParams.get('mode') === 'iframe';
    const [activeValue, setActiveValue] = useState(null);
    const contentBlocksRef = useRef(null);

    const isActive = (index) => isLivePreview && activeValue !== null && activeValue !== undefined && activeValue.toString() === index.toString();

    const handleClick = (index) => {
        if (isLivePreview) {
            // Toggle the active value
            const indexToSend = activeValue !== null && activeValue === index ? null : index;

            setActiveValue(indexToSend);
            // Send data to parent element
            window.parent.postMessage({
                type: POST_MESSAGE_TYPE.ACTIVE_CONTENT_BLOCK_CLICKED,
                payload: {
                    activeIndex: indexToSend
                }
            }, env.CMS_DOMAIN);
        }
    };

    useEffect(() => {
        if (isLivePreview && activeValue !== null && contentBlocksRef.current) {
            const activeBlock = contentBlocksRef.current.querySelector(`[data-block-index="${activeValue}"]`);
            if (activeBlock) {
                activeBlock.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [isLivePreview, activeValue]);

    useEffect(() => {
        // Add event listener for messages from the parent window for Live Preview
        if (isLivePreview) {
            const messageHandler = (event) => {
                // Make sure to validate the origin for security
                const allowedOrigins = [env.CMS_DOMAIN];
                if (!allowedOrigins.includes(event.origin)) {
                    return;
                }

                const message = event.data;

                // Handle URL update messages
                if (message && message.type === POST_MESSAGE_TYPE.UPDATE_HIGHLIGHTED_ITEM) {
                    const { activeIndex } = message.payload || {};
                    setActiveValue(activeIndex);
                }
            };

            window.addEventListener('message', messageHandler);

            // Clean up event listener on component unmount
            return () => {
                window.removeEventListener('message', messageHandler);
            };
        }
    }, [isLivePreview]);

    if (!data.length) {
        return null;
    }

    return (
        <div className="content-blocks" ref={contentBlocksRef}>
            {data.map((block, index) => {
                const blockProps = {
                    blockIndex: index,
                    pageTitle
                };
                const blockIndex = hasHeroCarousel ? index + 1 : index;

                const isHighlighted = isActive(blockIndex);

                const contentBlockContainerClass = classnames('content-block-container', {
                    'is-live-preview': isLivePreview,
                    'is-highlighted': isHighlighted
                });

                return isLivePreview ? (
                    <div
                        key={blockIndex}
                        className={contentBlockContainerClass}
                        onClick={() => handleClick(blockIndex)}
                        data-block-index={blockIndex}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                handleClick(blockIndex);
                            }
                        }}
                    >
                        {isHighlighted && <ActiveOverlay type={block.type} activeIndex={blockIndex} />}
                        {renderBlock(block, blockProps)}
                    </div>
                ) : renderBlock(block, blockProps);
            })}
        </div>
    );
};

ContentBlocks.propTypes = {
    data: PropTypes.array.isRequired,
    pageTitle: PropTypes.string,
    hasHeroCarousel: PropTypes.bool
};

ContentBlocks.defaultProps = {
    pageTitle: '',
    hasHeroCarousel: false
};

export default ContentBlocks;
